import React, { useEffect, useState } from "react";
import { Gift, X, Mail, CheckCircle2 } from "lucide-react";
import { useTracking } from "../contexts/TrackingContext";
import {
  getLocalCheckoutIntent,
  suppressCheckoutPopup,
  clearLocalCheckoutIntent,
} from "../lib/trackCheckoutIntent";

const DELAY_MS = 25000;
const ABANDON_DELAY_MS = 5000;
const DISMISS_KEY = "tdf_signup_dismissed";

// Pages where the popup would fight the interactive taster or checkout flow.
const SUPPRESS_PATHS = [
  "/online-assessment",
  "/cv",
  "/interview",
  "/psychometrics",
  "/my-account",
  "/admin",
  "/auth",
];

export default function FreeSignupPopup() {
  const { isSignedUp, signup } = useTracking();
  const [open, setOpen] = useState(false);
  const [intent, setIntent] = useState(null);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (isSignedUp) return;
    if (localStorage.getItem(DISMISS_KEY)) return;
    if (SUPPRESS_PATHS.some((p) => window.location.pathname.startsWith(p))) return;

    // If there's a recent local checkout intent for a guest (no email), show
    // an abandoned-cart variant sooner and allow quick recovery.
    try {
      const c = getLocalCheckoutIntent();
      if (c && !c.email) {
        setIntent(c);
        const t = setTimeout(() => setOpen(true), ABANDON_DELAY_MS);
        return () => clearTimeout(t);
      }
    } catch (e) {
      // ignore errors reading local intent
    }

    const t = setTimeout(() => setOpen(true), DELAY_MS);
    return () => clearTimeout(t);
  }, [isSignedUp]);

  const close = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setOpen(false);
  };

  const suppressAndClose = (hours = 24) => {
    try {
      suppressCheckoutPopup(hours);
    } catch (e) {}
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setErr("");
    try {
      await signup(email, firstName, "popup");
      // If there was a local checkout intent, attach/clear it locally so we
      // don't show the abandoned popup repeatedly.
      try {
        clearLocalCheckoutIntent();
      } catch (e) {}
      localStorage.setItem(DISMISS_KEY, "1");
      setSubmitted(true);
      setTimeout(() => setOpen(false), 2200);
    } catch (er) {
      setErr("Couldn't sign you up right now. Please try again.");
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center p-4 pointer-events-none">
      <div className="absolute inset-0 bg-black/40 pointer-events-auto" onClick={close} />
      <div className="relative pointer-events-auto bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-md w-full overflow-hidden">
        <button
          aria-label="Close"
          onClick={close}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center"
        >
          <X className="w-4 h-4 text-slate-600" />
        </button>
        <div className="bg-[#0F1B2E] text-white p-6">
          <div className="flex items-center gap-2 mb-2">
            <Gift className="w-5 h-5 text-[#E63B2E]" />
            <span className="text-xs font-bold uppercase tracking-widest text-[#E63B2E]">Free Account</span>
          </div>
          {intent ? (
            <>
              <h3 className="tdf-heading text-xl md:text-2xl">You left something in your cart</h3>
              <p className="text-slate-300 text-sm mt-1">Looks like you started a purchase — we can remind you or let you complete it now.</p>
            </>
          ) : (
            <>
              <h3 className="tdf-heading text-xl md:text-2xl">Get exclusive offers & discounts</h3>
              <p className="text-slate-300 text-sm mt-1">Free TDF account. Personalised railway career tips based on what you're reading. Unsubscribe any time.</p>
            </>
          )}
        </div>
        {submitted ? (
          <div className="p-6 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
            <p className="font-bold text-tdf-navy">You're in!</p>
            <p className="text-sm text-slate-600 mt-1">We'll personalise your future emails to topics you visit most.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-3">
            {intent && (
              <div className="space-y-3">
                <p className="font-semibold text-tdf-navy">{intent.product_title}</p>
                {typeof intent.product_price_pence === "number" && (
                  <p className="text-sm text-slate-500">Price: £{(intent.product_price_pence / 100).toFixed(2)}</p>
                )}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      try {
                        suppressCheckoutPopup(24);
                      } catch (e) {}
                      // Navigate to the stored product URL to continue checkout.
                      window.location.href = intent.product_url;
                    }}
                    className="btn-pill btn-primary text-sm !py-2.5 flex-1"
                  >
                    Complete purchase
                  </button>
                  <button
                    type="button"
                    onClick={() => suppressAndClose(24)}
                    className="w-36 text-xs text-slate-500 hover:text-slate-700"
                  >
                    Remind me later
                  </button>
                </div>
                <hr className="my-2" />
              </div>
            )}
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="First name (optional)"
              className="w-full rounded-lg border border-slate-300 focus:border-[#E63B2E] focus:ring-2 focus:ring-[#E63B2E]/20 outline-none px-4 py-2.5 text-sm"
            />
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="w-full rounded-lg border border-slate-300 focus:border-[#E63B2E] focus:ring-2 focus:ring-[#E63B2E]/20 outline-none pl-10 pr-4 py-2.5 text-sm"
              />
            </div>
            {err && <p className="text-xs text-rose-600">{err}</p>}
            <button type="submit" className="w-full btn-pill btn-primary text-sm !py-2.5">
              Create my free account
            </button>
            <button type="button" onClick={close} className="w-full text-xs text-slate-500 hover:text-slate-700">
              No thanks
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
